import React, { useMemo, useState, useRef, useEffect } from "react";
import { CalendarBlank, CaretDown, X } from "@phosphor-icons/react";

/**
 * Preset date-range picker for the dashboard. Emits ISO date strings
 * (YYYY-MM-DD) via `onChange({ start, end, label })`.
 * `start`/`end` are `null` for "All Time".
 */
const PRESETS = [
  { id: "all_time", label: "All Time" },
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "this_week", label: "This Week" },
  { id: "last_week", label: "Last Week" },
  { id: "this_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "custom", label: "Custom Range" },
];

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const startOfWeek = (d) => {
  // Monday as week start
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const r = new Date(d);
  r.setDate(d.getDate() + diff);
  return r;
};

export function computeRange(presetId, customStart, customEnd) {
  const now = new Date();
  switch (presetId) {
    case "today":
      return { start: toISO(now), end: toISO(now) };
    case "yesterday": {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      return { start: toISO(y), end: toISO(y) };
    }
    case "this_week": {
      const s = startOfWeek(now);
      return { start: toISO(s), end: toISO(now) };
    }
    case "last_week": {
      const s = startOfWeek(now);
      const start = new Date(s); start.setDate(s.getDate() - 7);
      const end = new Date(s); end.setDate(s.getDate() - 1);
      return { start: toISO(start), end: toISO(end) };
    }
    case "this_month": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: toISO(s), end: toISO(now) };
    }
    case "last_month": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: toISO(s), end: toISO(e) };
    }
    case "custom":
      return { start: customStart || null, end: customEnd || null };
    case "all_time":
    default:
      return { start: null, end: null };
  }
}

export default function DateRangePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState(value?.start || "");
  const [customEnd, setCustomEnd] = useState(value?.end || "");
  const [presetId, setPresetId] = useState(value?.presetId || "all_time");
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const activeLabel = useMemo(() => {
    const p = PRESETS.find((x) => x.id === presetId);
    if (presetId === "custom" && (customStart || customEnd)) {
      return `${customStart || "…"} → ${customEnd || "…"}`;
    }
    return p?.label || "All Time";
  }, [presetId, customStart, customEnd]);

  const apply = (id, cs = customStart, ce = customEnd) => {
    setPresetId(id);
    const range = computeRange(id, cs, ce);
    onChange({ ...range, presetId: id, label: PRESETS.find((p) => p.id === id)?.label || "" });
    if (id !== "custom") setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="date-range-picker">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="date-range-toggle"
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-[#111827] text-sm text-gray-200 hover:border-white/20 transition-colors"
      >
        <CalendarBlank size={14} weight="duotone" className="text-blue-400" />
        <span className="font-medium">{activeLabel}</span>
        <CaretDown size={12} />
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-72 bg-[#0B0F19] border border-white/10 rounded-lg shadow-2xl p-2"
          data-testid="date-range-panel"
        >
          <div className="grid grid-cols-2 gap-1 mb-2">
            {PRESETS.filter((p) => p.id !== "custom").map((p) => (
              <button
                key={p.id}
                data-testid={`preset-${p.id}`}
                onClick={() => apply(p.id)}
                className={`text-left text-xs px-2 py-1.5 rounded-md transition-colors ${
                  presetId === p.id
                    ? "bg-blue-500/20 text-blue-300 border border-blue-500/40"
                    : "text-gray-300 hover:bg-white/5 border border-transparent"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className={`border-t border-white/10 pt-2 rounded-md ${presetId === "custom" ? "bg-blue-500/5" : ""}`}>
            <p className="text-[10px] uppercase tracking-widest text-gray-500 px-1 mb-1">Custom Range</p>
            <div className="grid grid-cols-2 gap-1 px-1">
              <input
                type="date"
                data-testid="custom-start"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="bg-[#111827] border border-white/10 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-blue-500/60"
              />
              <input
                type="date"
                data-testid="custom-end"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-[#111827] border border-white/10 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-blue-500/60"
              />
            </div>
            <button
              data-testid="apply-custom"
              onClick={() => apply("custom", customStart, customEnd)}
              disabled={!customStart || !customEnd}
              className="mt-2 w-full text-xs py-1.5 rounded-md bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply Custom Range
            </button>
          </div>
          {presetId !== "all_time" && (
            <button
              data-testid="clear-range"
              onClick={() => apply("all_time")}
              className="mt-2 w-full flex items-center justify-center gap-1 text-[11px] py-1 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
            >
              <X size={11} /> Clear filter
            </button>
          )}
        </div>
      )}
    </div>
  );
}
